import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Section({ title, items, tone, testid }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testid}>
      <p className={`text-xs font-semibold uppercase tracking-wide ${tone}`}>{title}</p>
      <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">{items.map((x, i) => <li key={i}>{typeof x === "string" ? x : <>{x.text} <span className="text-[11px] text-slate-400">[{x.source}]</span></>}</li>)}</ul>
    </div>
  );
}

// "Explain" button + panel: deterministic facts from records, hedged suggestions, optional grounded AI wording.
export default function ExplainButton({ body, testid = "explain-btn", className = "" }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const run = async () => {
    setOpen(true); setD(null); setErr("");
    try { setD((await api.post("/fin/explain", body)).data); } catch (e) { setErr(apiErr(e)); }
  };
  return (
    <>
      <button className={`inline-flex items-center gap-1 text-xs text-violet-700 hover:underline ${className}`} onClick={run} data-testid={testid}><Sparkles className="h-3 w-3" />explain</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="explain-panel">
          <DialogHeader><DialogTitle>Why is this here?</DialogTitle></DialogHeader>
          {!d && !err && <p className="text-sm text-slate-500">Checking the records…</p>}
          {err && <p className="text-sm text-rose-600">{err}</p>}
          {d && <div className="space-y-3">
            <div className="rounded-lg border border-violet-100 bg-violet-50 p-3 text-sm" data-testid="explain-ai">
              <p className="mb-1 text-xs font-semibold text-violet-800">AI summary {d.ai.status === "ok" ? "(grounded in the records below)" : ""}</p>
              {d.ai.status === "ok" ? <p className="whitespace-pre-line">{d.ai.text.replace(/\*\*|__|^#+\s*/gm, "")}</p> : <p className="text-slate-600" data-testid="explain-ai-fallback">{d.ai.message}</p>}
            </div>
            <Section title="Facts (from Pawkt records)" items={d.facts} tone="text-slate-700" testid="explain-facts" />
            <Section title="Suggestions (not decisions)" items={d.suggestions} tone="text-amber-700" testid="explain-suggestions" />
            <Section title="Missing evidence" items={d.missing_evidence} tone="text-rose-700" testid="explain-missing" />
            {d.next_action && <p className="text-sm"><b>Next action:</b> {d.next_action}</p>}
            <p className="rounded bg-slate-50 p-2 text-[11px] text-slate-500">{d.note}</p>
          </div>}
        </DialogContent>
      </Dialog>
    </>
  );
}
